import React, { useState, useEffect } from "react";
import { FmBestSolution, FmCalculatedNpkCard, FmCompletedPercentage, FmMulticoteAgriCard, FmUpdateNpk } from "../Components";
import { FmAutocompleteInput, FmBack, FmButton, FmPageTitle, FmUnderlineNav, FmLoader, FmBlackPopup } from "../../../Components";
import { useHistory } from "react-router-dom";

import useStyles from "./CustomizeStagePage.styles";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useIsMobile } from "../../../Hooks";
import { AgriSolutionState, GeneralState } from "../../../Redux";
import { useSelector, useDispatch } from "react-redux";
import { BlendStat, Recipe } from "../../../Models";
import { RawMaterial } from "../../../Models/ServerModels/RawMaterials/GetAllRawMaterialsResponse";
import {
    NUTRIENTS_COUNT,
    ZERO,
    NITROGEN_MAX_LIMIT,
    PHOSPHORUS_COUNT,
    POTASSIUM_MAX_LIMIT,
    NUTRIENTS_SUM_MAX_LIMIT,
    HUNDRED,
    RECIPE_ITEMS_MAX_COUNT,
} from "../../../Utils/Consts";
import { LOGGED_IN_ROUTES } from "../../../Constants";
import { FmAllModals } from "../../../Components/Modals";

interface IFormInputs {
    coated: string;
    unCoated: string;
}

const CustomizeStagePage = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const history = useHistory();
    const selectToDisplayUpdateButton = useSelector(AgriSolutionState.Selectors.selectDisplayUpdateButton);
    const [showClearStepModal, setShowClearStepModal] = useState<boolean>(false);
    const [showSaveItemModal, setShowSaveItemModal] = useState<boolean>(false);
    const [displayUpdateFirstTime, setDisplayUpdateFirstTime] = useState<boolean>(selectToDisplayUpdateButton);
    const [displayUpdateButton, setDisplayUpdateButton] = useState<boolean>(false);
    const [hasSaveButton, setHasSaveButton] = useState<boolean>(false);
    const [displayRecipeItemCountErrorModal, setDisplayRecipeItemCountErrorModal] = useState<boolean>(false);
    const [ShowBlackSnackbar, setShowBlackSnackbar] = useState<boolean>(false);
    const isUserSave = useSelector(AgriSolutionState.Selectors.selectIsUserSave);
    const showClearThisStep = useSelector(GeneralState.Selectors.selectShowClearThisStepModal);
    const customizedData = useSelector(AgriSolutionState.Selectors.selectCustomizedState);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const removeItemFromRecipe = (item: BlendStat, isCoated: boolean) => {
        if (!displayUpdateFirstTime) setDisplayUpdateFirstTime(true);
        const currentRecipe: Recipe = customizedData.recipe;
        const itemIndex = isCoated ? currentRecipe.coated.findIndex((i) => i == item) : currentRecipe.unCoated.findIndex((i) => i == item);

        dispatch(AgriSolutionState.Actions.removeItemFromRecipe({ itemIndex, isCoated }));
    };
    useEffect(() => {
        if (customizedData.recipe.coated.length > 0 || customizedData.recipe.unCoated.length > 0) {
            setHasSaveButton(true);
        }
    }, [customizedData.recipe.coated, customizedData.recipe.unCoated]);

    useEffect(() => {
        (window as any).dataLayer.push({
            event: "CustomizeStage",
            pageUrl: "https://multimatch.haifa-group.com/multi-match/agri-solution/customize",
            pageTitle: "CustomizeStage",
        });
    }, []);

    useEffect(() => {
        if (customizedData.blendPercentages == HUNDRED && displayUpdateFirstTime) {
            dispatch(AgriSolutionState.Actions.setDisplayUpdateButton(true));
            setDisplayUpdateButton(true);
        } else {
            setDisplayUpdateButton(false);
        }
    }, [customizedData.blendPercentages]);

    const handleClear = () => {
        setValue("coated", undefined as any);
        setValue("unCoated", undefined as any);
        dispatch(AgriSolutionState.Actions.setRecipe({ coated: [], unCoated: [] }));
        dispatch(AgriSolutionState.Actions.setBlendPrecentage(0));
    };
    const addItemToRecipe = async (item: RawMaterial | any, isCoated: boolean) => {
        setValue("unCoated", undefined as any);
        if (!displayUpdateFirstTime) setDisplayUpdateFirstTime(true);
        const currentRecipe: Recipe = customizedData.recipe;
        const itemsAmount = currentRecipe.coated.length + currentRecipe.unCoated.length;
        if (itemsAmount > RECIPE_ITEMS_MAX_COUNT) {
            setDisplayRecipeItemCountErrorModal(true);
        } else {
            closeErrorModal();
            if (item && item.id) {
                const newItem: BlendStat = {
                    id: item.id,
                    name: item.name,
                    precentage: ZERO,
                };
                dispatch(AgriSolutionState.Actions.addItemToRecipe({ newItem, isCoated }));
            } else {
                try {
                    if (item) {
                        const isValid = isMaterialValid(item);
                        if (isValid) {
                            const formula = item.toString().split("-");
                            await dispatch(
                                AgriSolutionState.Thunks.addRawMaterials({
                                    N: Number(formula[0]),
                                    P2O5: Number(formula[1]),
                                    K2O: Number(formula[2]),
                                })
                            );
                        }
                    }
                } catch (e) {
                    //
                }
            }
        }
    };
    const isMaterialValid = (item: any): boolean => {
        if (item === undefined || item == null) {
            return true;
        }
        if (item.length === 0) {
            return true;
        }
        try {
            if (item && item.length > 0) {
                const formula = item.toString().split("-");
                if (formula.length !== NUTRIENTS_COUNT) return false;
                else {
                    const sum = Number(formula[0]) + Number(formula[1]) + Number(formula[2]);
                    return (
                        Number(formula[0]) <= NITROGEN_MAX_LIMIT &&
                        Number(formula[1]) <= PHOSPHORUS_COUNT &&
                        Number(formula[2]) <= POTASSIUM_MAX_LIMIT &&
                        sum <= NUTRIENTS_SUM_MAX_LIMIT
                    );
                }
            } else {
                return true;
            }
        } catch (e) {
            return false;
        }
    };
    const fetchNewRecipe = async () => {
        setShowLoader(true);
        await dispatch(AgriSolutionState.Thunks.changeRecipe());
        setShowLoader(false);
    };
    const updateBlendPercent = (item: BlendStat, isCoated: boolean, event: any) => {
        if (!displayUpdateFirstTime) setDisplayUpdateFirstTime(true);
        const value = event.target.value;
        dispatch(AgriSolutionState.Actions.setDisplayUpdateButton(true));
        dispatch(
            AgriSolutionState.Actions.updatePercentForItemInRecipe({
                item,
                isCoated,
                value,
            })
        );
    };
    const onNextClickHandler = () => {
        setHasSaveButton(false);
        setTimeout(() => {
            history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_RELEASE_CHART);
        }, 300);
    };
    const onBackClickHandler = () => {
        history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_FORTH_STEP);
    };

    const {
        setValue,
        control,
        getValues,
        register,
        formState: { errors },
    } = useForm<IFormInputs>({
        mode: "onBlur",
    });

    //------------ Modals ---------------------//
    const closeSaveItemModalHandle = () => {
        setShowSaveItemModal(false);
    };
    const closeErrorModal = () => {
        setDisplayRecipeItemCountErrorModal(false);
    };
    const openSaveItemModalHandle = () => {
        setShowSaveItemModal(true);
    };
    const closeClearModalHandle = () => {
        setShowClearStepModal(false);
    };
    const openClearModalHandle = () => {
        setShowClearStepModal(true);
    };
    const saveSolutionHandler = async () => {
        await dispatch(AgriSolutionState.Actions.setIsUserSave(true));
        await dispatch(AgriSolutionState.Thunks.saveProcess());

        if (isUserSave) {
            setShowBlackSnackbar(true);
        }
    };
    const closeSnackbarHandler = () => {
        setShowBlackSnackbar(false);
    };
    return (
        <>
            {ShowBlackSnackbar && (
                <div className={classes.snackbar_container}>
                    <FmBlackPopup text={t("saveItemPopup.snackbar")} snackbar={true} onClose={closeSnackbarHandler} />
                </div>
            )}
            <div className={classes.container}>
                <div className={isMobile ? classes.mobile_mainTitle_container : classes.mainTitle_container}>
                    <FmPageTitle title={t("pageStep5.title")} />
                    {!isMobile && (
                        <FmUnderlineNav
                            title={t("saveItemPopup.save")}
                            onClick={
                                hasSaveButton && !isUserSave
                                    ? openSaveItemModalHandle
                                    : hasSaveButton && isUserSave
                                    ? saveSolutionHandler
                                    : () => {
                                          //
                                      }
                            }
                            disabled={!hasSaveButton}
                        />
                    )}
                </div>
                <div className={classes.bestSolution_container}>
                    <FmBestSolution
                        leftText={`${t("pageStep5.bestSolution")}: `}
                        centerText={customizedData.bestSolutionFormula}
                        rightText={customizedData.bestSuitFormulaCoatings}
                    />
                </div>
                <div className={classes.line}></div>
                <div className={isMobile ? classes.mobile_subTitles_container : classes.subTitles_container}>
                    <span className={isMobile ? classes.mobile_selectTitle : classes.selectTitle}>{t("pageStep5.subTitle1")}</span>
                    <FmCompletedPercentage percentage={customizedData.blendPercentages} />
                </div>
                {isMobile ? (
                    <>
                        <div className={classes.mobile_inputs_container}>
                            <div className={isMobile ? classes.mobile_inputWrapper : classes.inputWrapper}>
                                <Controller
                                    name={"coated"}
                                    control={control}
                                    rules={{
                                        required: {
                                            value: false,
                                            message: t("validationErrors.fieldIsRequired"),
                                        },
                                    }}
                                    render={({ field }) => {
                                        return (
                                            <FmAutocompleteInput
                                                {...register("coated")}
                                                {...field}
                                                label={t("pageStep5.coated")}
                                                error={errors.coated}
                                                helperText={errors.coated?.message || ""}
                                                data={customizedData.allCoatedMaterials ? customizedData.allCoatedMaterials : []}
                                                getLabel={(item: any) => item.name}
                                                setValue={(item: any | undefined) => {
                                                    setValue("coated", item && item.name ? item.name : undefined);
                                                    addItemToRecipe(item, true);
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className={classes.leftMaterialArray}>
                                {customizedData.recipe.coated &&
                                    customizedData.recipe.coated.map((co: any) => (
                                        <FmMulticoteAgriCard
                                            key={co.name}
                                            name={co.name}
                                            percentage={co.precentage}
                                            onRemove={() => removeItemFromRecipe(co, true)}
                                            onUpdate={() => {
                                                updateBlendPercent(co, true, event);
                                            }}
                                        />
                                    ))}
                            </div>
                            <div className={isMobile ? classes.mobile_inputWrapper : classes.inputWrapper}>
                                <Controller
                                    name={"unCoated"}
                                    key={`unCoated-0`}
                                    control={control}
                                    rules={{
                                        required: {
                                            value: false,
                                            message: t("validationErrors.fieldIsRequired"),
                                        },
                                        validate: (value: any) => {
                                            return isMaterialValid(value);
                                        },
                                    }}
                                    render={({ field }) => {
                                        return (
                                            <FmAutocompleteInput
                                                {...register("unCoated")}
                                                {...field}
                                                label={t("pageStep5.uncoated")}
                                                error={errors.unCoated}
                                                noOptionsText={true}
                                                freeSolo
                                                //  autoSelect
                                                defaultValue={undefined}
                                                helperText={
                                                    errors.unCoated?.message || <span className={classes.helperText}>{t("pageStep5.uncoatedHelperText")}</span>
                                                }
                                                data={customizedData.allUnCoatedMaterials ? customizedData.allUnCoatedMaterials : []}
                                                getLabel={(item: any) => (item.name ? item.name : item)}
                                                setValue={(item: any) => {
                                                    setValue("unCoated", item && item.name ? item.name : item);
                                                    if (item !== undefined) addItemToRecipe(item, false);
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className={classes.rightMaterialArray}>
                                {customizedData.recipe.unCoated &&
                                    customizedData.recipe.unCoated.map((co: any) => (
                                        <FmMulticoteAgriCard
                                            key={co.name}
                                            name={co.name}
                                            percentage={co.precentage}
                                            onRemove={() => removeItemFromRecipe(co, false)}
                                            onUpdate={() => {
                                                updateBlendPercent(co, false, event);
                                            }}
                                        />
                                    ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={classes.inputs_container}>
                            <div className={classes.inputWrapper}>
                                <Controller
                                    name={"coated"}
                                    control={control}
                                    rules={{
                                        required: {
                                            value: false,
                                            message: t("validationErrors.fieldIsRequired"),
                                        },
                                    }}
                                    render={({ field }) => {
                                        return (
                                            <FmAutocompleteInput
                                                {...register("coated")}
                                                {...field}
                                                label={t("pageStep5.coated")}
                                                error={errors.coated}
                                                helperText={errors.coated?.message || ""}
                                                data={customizedData.allCoatedMaterials ? customizedData.allCoatedMaterials : []}
                                                getLabel={(item: any) => item.name}
                                                setValue={(item: any) => {
                                                    setValue("coated", item && item.name ? item.name : undefined);
                                                    addItemToRecipe(item, true);
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className={classes.inputWrapper}>
                                <Controller
                                    name={"unCoated"}
                                    key={`unCoated-0`}
                                    control={control}
                                    // validation for N P K
                                    rules={{
                                        required: {
                                            value: false,
                                            message: t("validationErrors.fieldIsRequired"),
                                        },
                                        validate: (value: any) => {
                                            return isMaterialValid(value);
                                        },
                                    }}
                                    render={({ field }) => {
                                        return (
                                            <FmAutocompleteInput
                                                {...register("unCoated")}
                                                {...field}
                                                label={t("pageStep5.uncoated")}
                                                error={errors.unCoated}
                                                noOptionsText={true}
                                                freeSolo
                                                defaultValue={undefined}
                                                //  autoSelect
                                                helperText={
                                                    errors.unCoated?.message || <span className={classes.helperText}>{t("pageStep5.uncoatedHelperText")}</span>
                                                }
                                                data={customizedData.allUnCoatedMaterials ? customizedData.allUnCoatedMaterials : []}
                                                getLabel={(item: any) => (item.name ? item.name : item)}
                                                setValue={(item: any) => {
                                                    setValue("unCoated", item && item.name ? item.name : item);
                                                    item && addItemToRecipe(item, false);
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                        </div>
                        <div className={classes.materialArray_container}>
                            <div className={classes.leftMaterialArray}>
                                {customizedData.recipe.coated &&
                                    customizedData.recipe.coated.map((co: any) => (
                                        <FmMulticoteAgriCard
                                            key={co.name}
                                            name={co.name}
                                            percentage={co.precentage}
                                            onRemove={() => removeItemFromRecipe(co, true)}
                                            onUpdate={() => {
                                                updateBlendPercent(co, true, event);
                                            }}
                                        />
                                    ))}
                            </div>
                            <div className={classes.rightMaterialArray}>
                                {customizedData.recipe.unCoated &&
                                    customizedData.recipe.unCoated.map((co: any) => (
                                        <FmMulticoteAgriCard
                                            key={co.name}
                                            name={co.name}
                                            percentage={co.precentage}
                                            onRemove={() => removeItemFromRecipe(co, false)}
                                            onUpdate={() => {
                                                updateBlendPercent(co, false, event);
                                            }}
                                        />
                                    ))}
                            </div>
                        </div>
                    </>
                )}

                {displayUpdateButton && (
                    <div className={classes.update_container}>{!showLoader ? <FmUpdateNpk onUpdate={fetchNewRecipe} /> : <FmLoader size={"verySmall"} />}</div>
                )}

                <div className={classes.cardContainer}>
                    <FmCalculatedNpkCard
                        n={customizedData.formulaNutrients.n}
                        p={customizedData.formulaNutrients.p}
                        k={customizedData.formulaNutrients.k}
                        missingPercentage={customizedData.blendPercentages ? HUNDRED - customizedData.blendPercentages : ZERO}
                    />
                </div>
                <div className={classes.buttons_container}>
                    {!isMobile && (
                        <div className={classes.buttons_container_leftSide}>
                            <FmBack onClick={onBackClickHandler} />
                        </div>
                    )}
                    <div className={isMobile ? classes.mobile_buttons_container_rightSide : classes.buttons_container_rightSide}>
                        <FmUnderlineNav title={t("pageStep5.clear")} onClick={showClearThisStep ? openClearModalHandle : handleClear} />
                        <FmButton onClick={onNextClickHandler}>{t("pageStep5.continueButton")}</FmButton>
                    </div>
                </div>
                <FmAllModals
                    clearModalIsOpen={showClearStepModal}
                    clearModalOnClose={closeClearModalHandle}
                    clearModalAction={handleClear}
                    saveItemModalIsOpen={showSaveItemModal}
                    saveItemModalOnClose={closeSaveItemModalHandle}
                    saveItemModalAction={saveSolutionHandler}
                    SaveProcessPromptWhen={hasSaveButton}
                    SaveProcessPromptMessage={t("saveProcessPopup.subTitle")}
                    ErrorRecipeIsOpen={displayRecipeItemCountErrorModal}
                    ErrorRecipeOnClose={closeErrorModal}
                    ErrorRecipeData={t("pageStep5.recipeValidationError")}
                />
            </div>
        </>
    );
};

export default CustomizeStagePage;
